/* eslint-disable no-nested-ternary */
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import { Content, ContentInteractionAction } from '../../../common/models/content/Content';
import { ContentArticleViewSkeleton } from './ContentArticleViewSkeleton';
import { getContentById, getContentStatus } from '../../../store/thunks/contentThunk';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectIsContentPreview } from '../../../store/slices/contentSlice';
import { UpdateShowDashboardToolbar } from '../../../store/slices/dashboardSlice';
import { TestIds } from '../../../assets/testIds';
import { HOME_ROUTE } from '../../../routes/namedRoutes';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useIsDesktop } from '../../../common/hooks/useResponsive';
import { useSafeBack } from '../../../common/hooks/useSafeBack';
import { Notfound } from '../../NotFound';
import { ArticleContentDisplay } from './ArticleContentDisplay';
import ContentShareSheet from './ContentShareSheet';
import OptimusButton from '../../../common/library/Button/Button';
import { colorTokens } from '../../../assets/primitiveColorTokens';

const backBtnStyle = (theme: Theme) => ({
  gap: theme.spacing(0.625),
  padding: `${theme.spacing(0.9375)} ${theme.spacing(1.25)} ${theme.spacing(0.9375)} ${theme.spacing(0.9375)}`,
  minWidth: 0,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  fontSize: theme.spacing(1.5),
  lineHeight: theme.spacing(2),
  fontWeight: 400,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.common.white,
  '& .MuiButton-startIcon': { m: 0 },
  '&:hover': {
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.action.hover,
  },
} as const);

export function ContentArticleView() {
  const { id } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const [content, setContent] = useState<Content>();
  const [hasError, setHasError] = useState(false);
  const dispatch = useAppDispatch();
  const { trackContentInteraction } = useCustomEventTracking();
  const isDesktop = useIsDesktop();
  const isContentPreview = useAppSelector(selectIsContentPreview);
  const [isSaved, setIsSaved] = useState(false);
  const safeBack = useSafeBack(HOME_ROUTE);

  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    dispatch(UpdateShowDashboardToolbar(true));
    return () => {
      // hide toolbar on unmount
      dispatch(UpdateShowDashboardToolbar(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setHasError(false);
    const getContentByIdResponse = dispatch(getContentById(Number(id))).then(unwrapResult);
    const getContentStatusResponse = dispatch(getContentStatus(id || '')).then(unwrapResult);
    Promise.all([
      getContentByIdResponse,
      getContentStatusResponse,
    ])
      .then(([contentRes, contentStatusRes]) => {
        setContent(contentRes);
        if (contentStatusRes && contentStatusRes[Number(id)]) {
          setIsSaved(contentStatusRes[id || ''].save);
        }
      })
      .catch(() => {
        setHasError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fireContentInteractionEvent = (action: ContentInteractionAction, options?: {
    [k: string]: unknown;
  }) => {
    trackContentInteraction(
      action,
      content?.attributes?.type?.replace(/^\w/, (c) => c.toUpperCase()) || '',
      {
        contentId: content?.id as number,
        contentTitle: content?.attributes?.title || '',
        contentCreatorName: content?.attributes?.content_owner?.data?.attributes?.display_name || '',
        contentMetaData: content?.attributes?.metadata || [],
        type: content?.attributes?.type || '',
        action,
        ...options,
      },
    );
  };

  const fireContentSavedEvent = () => {
    setIsSaved(!isSaved);
    fireContentInteractionEvent(
      isSaved ? ContentInteractionAction.Unsave : ContentInteractionAction.Save,
    );
  };

  const articleActions = (
    <>
      <OptimusButton
        size='s'
        fullWidth={false}
        minWidth='auto'
        data-testid={TestIds.contentArticleView.saveBtn}
        customVariant={isSaved ? 'secondaryGray' : 'primary'}
        buttonText={isSaved ? `${t('navigation.saved')}` : `${t('content.action.save')}`}
        // Preview content isn't live, so it can't be saved or shared (same as the preview cards)
        disabled={isContentPreview}
        onClick={fireContentSavedEvent}
      />
      <OptimusButton
        size='s'
        fullWidth={false}
        minWidth='auto'
        data-testid={TestIds.contentArticleView.shareBtn}
        customVariant='secondaryGray'
        sx={{ borderColor: colorTokens.color.gray[300], color: theme.palette.text.secondary }}
        buttonText={`${t('content.action.share')}`}
        disabled={isContentPreview}
        onClick={() => setIsShareOpen(true)}
      />
    </>
  );

  return (
    <>
      <Box sx={{
        // On mobile the dashboard layout already pads the page by 16px
        px: theme.spacing(isDesktop ? 3 : 0.5),
        pt: theme.spacing(isDesktop ? 2 : 0),
      }}>
        <Button
          data-testid={TestIds.contentArticleView.backBtn}
          startIcon={<ChevronLeftIcon sx={{ fontSize: `${theme.spacing(2)} !important` }} />}
          sx={backBtnStyle}
          onClick={safeBack}
        >
          {t('button.backToFeed')}
        </Button>
      </Box>
      <Container maxWidth={false}
        disableGutters
        sx={{
          width: '100%',
          px: theme.spacing(4),
          pt: theme.spacing(4),
          paddingBottom: theme.spacing(6),
          backgroundColor: theme.palette.common.white,
          [theme.breakpoints.down('md')]: {
            p: `${theme.spacing(2.5)} ${theme.spacing(0.5)} ${theme.spacing(5)}`,
            marginTop: 0,
          },
        }}>
        {hasError ? <Notfound /> :
          !content ? <ContentArticleViewSkeleton showActions /> :
            <ArticleContentDisplay
              content={content}
              actions={articleActions}
            />
        }
      </Container >
      <Dialog
        open={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        PaperProps={{
          sx: { borderRadius: theme.spacing(2), m: theme.spacing(3), width: theme.spacing(100) },
        }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: theme.spacing(2, 3),
        }}>
          <Typography sx={{ fontSize: theme.spacing(2.5), fontWeight: 500 }}>
            {t('content.action.share')}
          </Typography>
          <IconButton aria-label={`${t('button.close')}`} onClick={() => setIsShareOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: theme.spacing(5) }}>
          <ContentShareSheet
            shareUrl={window.location.href}
            fireContentInteractionEvent={fireContentInteractionEvent}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
